import { AnimatePresence, motion } from 'framer-motion';
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Footer from '../components/Footer';
import Navbar from '../components/Navbar';
import useSEO from '../hooks/useSEO';
import apiService from '../services/api';

const TemplatesPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [usingMockData, setUsingMockData] = useState(false);
  const [showPopup, setShowPopup] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);

  // Filters state
  const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || '');
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get('category') || '');
  const [priceFilter, setPriceFilter] = useState(searchParams.get('price') || 'all');
  const [sortBy, setSortBy] = useState(searchParams.get('sort') || 'newest');

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalTemplates, setTotalTemplates] = useState(0);

  const templatesPerPage = 24;
  const [categoryOptions, setCategoryOptions] = useState([
    { value: '', label: 'All Categories' }
  ]);

  const baseUrl = typeof window !== 'undefined' ? window.location.origin : '';

  useSEO({
    title: 'Design Templates | Kigo Prints - Professional Printing Templates',
    description: 'Browse our collection of professional design templates for business cards, stickers, flyers, and more. Customize and print with Kigo Prints.',
    keywords: 'design templates, printing templates, business card templates, sticker templates, flyer templates, custom templates, Kigo Prints',
    image: `${baseUrl}/logo_white_theme.png`,
    structuredData: {
      '@context': 'https://schema.org',
      '@type': 'CollectionPage',
      'name': 'Design Templates',
      'description': 'Professional design templates for printing services',
      'url': `${baseUrl}/templates`,
      'provider': {
        '@type': 'Organization',
        'name': 'Kigo Prints',
        'url': baseUrl
      }
    }
  });

  // Fetch categories from API
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await apiService.request('/products/categories/active');
        if (response.status === 'success' && response.data.categories) {
          const options = [
            { value: '', label: 'All Categories' },
            ...response.data.categories.map(cat => ({
              value: cat.name,
              label: cat.name.split('-').map(word =>
                word.charAt(0).toUpperCase() + word.slice(1)
              ).join(' ')
            }))
          ];
          setCategoryOptions(options);
        }
      } catch (error) {
        console.error('Failed to fetch categories:', error);
      }
    };
    fetchCategories();
  }, []);

  // Load templates with filters
  const loadTemplates = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const filters = {
        page: currentPage,
        limit: templatesPerPage,
        ...(selectedCategory && { category: selectedCategory }),
        ...(searchTerm && { search: searchTerm }),
        ...(priceFilter === 'free' && { free: true }),
        ...(priceFilter === 'premium' && { free: false }),
        sortBy: sortBy === 'newest' ? 'createdAt' :
          sortBy === 'oldest' ? 'createdAt' :
            sortBy === 'price-low' ? 'price' :
              sortBy === 'price-high' ? 'price' :
                sortBy === 'popular' ? 'analytics.usage' : 'createdAt',
        sortOrder: sortBy === 'oldest' || sortBy === 'price-low' ? 'asc' : 'desc'
      };

      const response = await apiService.getTemplates(filters);

      if (response.status === 'success') {
        setTemplates(response.data.templates || []);
        setTotalPages(response.pagination?.pages || 1);
        setTotalTemplates(response.pagination?.total || 0);
        setUsingMockData(false);
      } else {
        throw new Error(response.message || 'Failed to load templates');
      }

    } catch (err) {
      console.error('Error loading templates:', err);
      setError(err.message || 'Failed to load templates');
      // Fallback to mock data for demonstration
      setTemplates(getMockTemplates());
      setTotalTemplates(getMockTemplates().length);
      setTotalPages(Math.ceil(getMockTemplates().length / templatesPerPage));
      setUsingMockData(true);
    } finally {
      setLoading(false);
    }
  }, [currentPage, selectedCategory, searchTerm, priceFilter, sortBy, templatesPerPage]);

  // Mock data fallback
  const getMockTemplates = () => [
    {
      id: 1,
      name: 'Modern Business Card',
      category: 'business-cards',
      thumbnail: 'https://i.pinimg.com/736x/3e/73/67/3e736712a10e8b84bbb33cc9a058ec5b.jpg',
      price: 0,
      featured: true,
      description: 'Clean and modern design perfect for professionals',
      tags: ['professional', 'minimal', 'clean'],
      canvaLink: 'https://www.canva.com/design/DAGXyz123/edit'
    },
    {
      id: 2,
      name: 'Creative Business Card',
      category: 'business-cards',
      thumbnail: 'https://i.pinimg.com/736x/1c/e2/57/1ce257a50d0e935b078aa24a342f5b7a.jpg',
      price: 5,
      featured: false,
      description: 'Bold and creative design for artistic professionals',
      tags: ['creative', 'bold', 'artistic'],
      canvaLink: 'https://www.canva.com/design/DAGAbc456/edit'
    },
    {
      id: 3,
      name: 'Elegant Business Card',
      category: 'business-cards',
      thumbnail: 'https://i.pinimg.com/736x/51/d4/a3/51d4a35685ae5f10686fab981b3dfd0b.jpg',
      price: 0,
      featured: true,
      description: 'Sophisticated design with elegant typography',
      tags: ['elegant', 'sophisticated', 'luxury'],
      canvaLink: ''
    },
    {
      id: 4,
      name: 'Wedding Invitation',
      category: 'wedding-invites',
      thumbnail: 'https://i.pinimg.com/736x/1a/78/64/1a7864088f21815d8022e06f1e5f9b86.jpg',
      price: 10,
      featured: false,
      description: 'Beautiful wedding invitation design',
      tags: ['wedding', 'elegant', 'floral'],
      canvaLink: 'https://www.canva.com/design/DAGDef789/edit'
    }
  ];

  // Update URL params when filters change
  useEffect(() => {
    const params = new URLSearchParams();
    if (searchTerm) params.set('search', searchTerm);
    if (selectedCategory) params.set('category', selectedCategory);
    if (priceFilter !== 'all') params.set('price', priceFilter);
    if (sortBy !== 'newest') params.set('sort', sortBy);
    setSearchParams(params);
  }, [searchTerm, selectedCategory, priceFilter, sortBy, setSearchParams]);

  // Load templates when filters or pagination change
  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

  const handleTemplateClick = (template) => {
    setSelectedTemplate(template);
    setShowPopup(true);
  };

  const handleClosePopup = () => {
    setShowPopup(false);
    setSelectedTemplate(null);
  };

  const handleGetBackToSite = () => {
    handleClosePopup();
  };

  const handleOrderByUpload = () => {
    handleClosePopup();
    if (selectedTemplate?.category) {
      navigate(`/category/${selectedTemplate.category}`);
    } else {
      navigate('/');
    }
  };

  const handleOpenCanva = () => {
    if (selectedTemplate?.canvaLink) {
      window.open(selectedTemplate.canvaLink, '_blank', 'noopener,noreferrer');
    }
    handleClosePopup();
  };

  // Use templates directly from API (already filtered and sorted by backend)
  // Only apply client-side filtering/sorting when using mock data (error fallback)
  const displayTemplates = usingMockData ? (() => {
    const filtered = templates.filter(template => {
      const matchesSearch = !searchTerm ||
        template.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        template.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        template.tags?.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()));

      const matchesCategory = !selectedCategory || template.category === selectedCategory;

      const matchesPrice = priceFilter === 'all' ||
        (priceFilter === 'free' && template.price === 0) ||
        (priceFilter === 'premium' && template.price > 0);

      return matchesSearch && matchesCategory && matchesPrice;
    });

    return [...filtered].sort((a, b) => {
      switch (sortBy) {
        case 'oldest':
          return (a.id || a._id) - (b.id || b._id);
        case 'price-low':
          return (a.price || 0) - (b.price || 0);
        case 'price-high':
          return (b.price || 0) - (a.price || 0);
        case 'name':
          return (a.name || '').localeCompare(b.name || '');
        default: // newest
          return (b.id || b._id) - (a.id || a._id);
      }
    });
  })() : templates;

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <Navbar />
      <main className="flex-grow pt-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

          {/* Header */}
          <div className="text-center mb-8 sm:mb-12">
            <motion.h1
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-2xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-2 sm:mb-4"
            >
              Design Templates
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="text-sm sm:text-xl text-gray-600 max-w-3xl mx-auto"
            >
              Browse our collection of professional templates. Click any template to customize it in Canva and make it your own.
            </motion.p>
          </div>

          {/* Filters and Search */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white rounded-xl shadow-md border border-gray-200 p-4 sm:p-6 mb-6 sm:mb-8"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">

              {/* Search */}
              <div className="lg:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Search Templates
                </label>
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Search by name, description, or tags..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 sm:py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#008000] focus:border-[#008000] transition-colors text-sm sm:text-base"
                  />
                  <svg className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path>
                  </svg>
                </div>
              </div>

              {/* Category Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Category
                </label>
                <select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  className="w-full py-2 sm:py-3 px-3 sm:px-4 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#008000] focus:border-[#008000] transition-colors bg-white text-sm sm:text-base"
                >
                  {categoryOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Price Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Price
                </label>
                <select
                  value={priceFilter}
                  onChange={(e) => setPriceFilter(e.target.value)}
                  className="w-full py-2 sm:py-3 px-3 sm:px-4 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#008000] focus:border-[#008000] transition-colors bg-white text-sm sm:text-base"
                >
                  <option value="all">All Templates</option>
                  <option value="free">Free Templates</option>
                  <option value="premium">Premium Templates</option>
                </select>
              </div>
            </div>

            {/* Sort and Results Info */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mt-6 pt-6 border-t border-gray-200">
              <div className="flex items-center space-x-4">
                <label className="text-sm font-medium text-gray-700">Sort by:</label>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="py-2 px-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#008000] focus:border-[#008000] transition-colors bg-white"
                >
                  <option value="newest">Newest First</option>
                  <option value="oldest">Oldest First</option>
                  <option value="price-low">Price: Low to High</option>
                  <option value="price-high">Price: High to Low</option>
                  <option value="name">Name A-Z</option>
                  <option value="popular">Most Popular</option>
                </select>
              </div>

              <div className="mt-4 sm:mt-0 text-sm text-gray-600">
                {loading ? 'Loading...' : `${usingMockData ? displayTemplates.length : totalTemplates} templates found`}
              </div>
            </div>
          </motion.div>

          {/* Loading State */}
          {loading && (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
              {Array.from({ length: 12 }).map((_, index) => (
                <div key={index} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden animate-pulse">
                  <div className="aspect-square bg-gray-200"></div>
                  <div className="p-4">
                    <div className="h-4 bg-gray-200 rounded-lg mb-2"></div>
                    <div className="h-3 bg-gray-200 rounded-lg w-2/3"></div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Error State */}
          {error && !loading && (
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="text-center py-12"
            >
              <div className="bg-white rounded-xl shadow-md border border-gray-200 p-8 max-w-md mx-auto">
                <svg className="w-16 h-16 text-red-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                </svg>
                <h3 className="text-lg font-medium text-gray-900 mb-2">Error Loading Templates</h3>
                <p className="text-gray-600 mb-4">{error}</p>
                <button
                  onClick={loadTemplates}
                  className="bg-[#008000] text-white px-4 sm:px-6 py-1.5 sm:py-2 rounded-lg hover:bg-[#008000] transition-colors font-medium text-sm sm:text-base"
                >
                  Try Again
                </button>
              </div>
            </motion.div>
          )}

          {/* Templates Grid */}
          {!loading && !error && (
            <>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.3 }}
                className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6"
              >
                <AnimatePresence>
                  {displayTemplates.map((template, index) => (
                    <motion.div
                      key={template.id || template._id}
                      layout
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.9 }}
                      transition={{ delay: index * 0.05 }}
                      whileHover={{ y: -5 }}
                      onClick={() => handleTemplateClick(template)}
                      className="bg-white rounded-xl shadow-sm border border-gray-200 cursor-pointer transition-all duration-300 hover:shadow-lg hover:border-[#008000] group flex flex-col"
                    >
                      {/* Template Preview */}
                      <div className="relative overflow-hidden rounded-t-xl aspect-square">
                        <img
                          src={template.thumbnail || template.previewImage?.url}
                          alt={template.name}
                          className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                        />

                        {/* Overlay badges */}
                        <div className="absolute top-3 left-3 flex gap-2">
                          {template.featured && (
                            <span className="bg-orange-500 text-white text-xs px-2 py-1 rounded-lg font-medium">
                              Featured
                            </span>
                          )}
                          {template.price === 0 ? (
                            <span className="bg-[#008000] text-white text-xs px-2 py-1 rounded-lg font-medium">
                              Free
                            </span>
                          ) : (
                            <span className="bg-[#008000] text-white text-xs px-2 py-1 rounded-lg font-medium">
                              ₹{template.price}
                            </span>
                          )}
                        </div>

                        {/* Hover overlay */}
                        <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-opacity duration-300 flex items-center justify-center">
                          <motion.div
                            initial={{ opacity: 0, scale: 0.8 }}
                            whileHover={{ opacity: 1, scale: 1 }}
                            className="bg-white rounded-full p-3 shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                          >
                            <svg className="w-6 h-6 text-[#008000]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"></path>
                            </svg>
                          </motion.div>
                        </div>
                      </div>

                      {/* Template Info */}
                      <div className="p-3 sm:p-4 flex flex-col h-full">
                        <div>
                          <h4 className="font-semibold text-sm sm:text-base text-gray-900 mb-1 truncate">{template.name}</h4>
                          <p className="text-xs sm:text-sm text-gray-600 mb-2 line-clamp-1">{template.description}</p>

                          {/* Tags */}
                          {template.tags && template.tags.length > 0 && (
                            <div className="flex flex-wrap gap-1 -mb-24">
                              {template.tags.slice(0, 2).map((tag, tagIndex) => (
                                <span
                                  key={tagIndex}
                                  className="bg-gray-100 text-gray-600 text-xs px-2 py-1 rounded-full"
                                >
                                  {tag}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>

                        {/* Action Button - Always at bottom */}
                        <button className="w-full bg-[#008000] hover:bg-[#008000] text-white text-xs sm:text-sm font-medium py-1.5 sm:py-2 px-2 sm:px-3 rounded-xl transition-all duration-200 flex items-center justify-center gap-1.5 sm:gap-2 mt-auto">
                          <svg className="w-3 h-3 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"></path>
                          </svg>
                          Edit in Canva
                        </button>
                      </div>
                    </motion.div>
                  ))}
                </AnimatePresence>
              </motion.div>

              {/* Pagination */}
              {totalPages > 1 && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.5 }}
                  className="flex items-center justify-center mt-8 sm:mt-12"
                >
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                      disabled={currentPage === 1}
                      className="px-3 sm:px-4 py-1.5 sm:py-2 border border-gray-300 rounded-lg hover:bg-gray-50 hover:border-[#008000] hover:text-[#008000] disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm sm:text-base"
                    >
                      Previous
                    </button>

                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      let pageNum;
                      if (totalPages <= 5) {
                        pageNum = i + 1;
                      } else if (currentPage <= 3) {
                        pageNum = i + 1;
                      } else if (currentPage >= totalPages - 2) {
                        pageNum = totalPages - 4 + i;
                      } else {
                        pageNum = currentPage - 2 + i;
                      }

                      return (
                        <button
                          key={pageNum}
                          onClick={() => setCurrentPage(pageNum)}
                          className={`px-3 sm:px-4 py-1.5 sm:py-2 border rounded-lg transition-colors text-sm sm:text-base ${currentPage === pageNum
                            ? 'bg-[#008000] text-white border-[#008000]'
                            : 'border-gray-300 hover:bg-gray-50 hover:border-[#008000] hover:text-[#008000]'
                            }`}
                        >
                          {pageNum}
                        </button>
                      );
                    })}

                    <button
                      onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                      disabled={currentPage === totalPages}
                      className="px-3 sm:px-4 py-1.5 sm:py-2 border border-gray-300 rounded-lg hover:bg-gray-50 hover:border-[#008000] hover:text-[#008000] disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm sm:text-base"
                    >
                      Next
                    </button>
                  </div>
                </motion.div>
              )}

              {/* No Results */}
              {displayTemplates.length === 0 && !loading && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="text-center py-16"
                >
                  <div className="bg-white rounded-xl shadow-md border border-gray-200 p-8 max-w-md mx-auto">
                    <svg className="w-16 h-16 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.172 16.172a4 4 0 015.656 0M9 12h6m-6-4h6m2 5.291A7.962 7.962 0 0112 20a7.962 7.962 0 01-5-1.709v-2.582a6.001 6.001 0 100-11.418v-2.582A7.962 7.962 0 0112 4a7.962 7.962 0 015 1.709v2.582a6.001 6.001 0 100 11.418v2.582A7.962 7.962 0 0112 20z"></path>
                    </svg>
                    <h3 className="text-lg font-medium text-gray-900 mb-2">No Templates Found</h3>
                    <p className="text-gray-600 mb-4">Try adjusting your search criteria or browse all categories.</p>
                    <button
                      onClick={() => {
                        setSearchTerm('');
                        setSelectedCategory('');
                        setPriceFilter('all');
                      }}
                      className="bg-[#008000] text-white px-4 sm:px-6 py-1.5 sm:py-2 rounded-lg hover:bg-[#008000] transition-colors font-medium text-sm sm:text-base"
                    >
                      Clear Filters
                    </button>
                  </div>
                </motion.div>
              )}
            </>
          )}
        </div>
      </main>
      <Footer />

      {/* Template Popup Modal */}
      {showPopup && selectedTemplate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" onClick={handleClosePopup}>
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 sm:p-8 relative"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={handleClosePopup}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
              </svg>
            </button>

            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-[#008000]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
                </svg>
              </div>
              <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">Template Selected</h3>
              <p className="text-sm sm:text-base text-gray-600">Choose an option to proceed</p>
            </div>

            <div className="space-y-3 sm:space-y-4">
              {selectedTemplate.canvaLink && (
                <button
                  onClick={handleOpenCanva}
                  className="w-full bg-gradient-to-r from-purple-500 to-blue-500 hover:from-purple-600 hover:to-blue-600 text-white font-semibold py-3 sm:py-4 px-4 rounded-xl transition-all duration-200 flex items-center justify-center gap-2 shadow-md hover:shadow-lg transform hover:scale-[1.02] active:scale-[0.98]"
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
                  </svg>
                  Edit in Canva
                </button>
              )}

              <button
                onClick={handleOrderByUpload}
                className="w-full bg-[#008000] hover:bg-[#008000] text-white font-semibold py-3 sm:py-4 px-4 rounded-xl transition-all duration-200 flex items-center justify-center gap-2 shadow-md hover:shadow-lg transform hover:scale-[1.02] active:scale-[0.98]"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"></path>
                </svg>
                Order by uploading your design (PDF or PNG)
              </button>

              <button
                onClick={handleGetBackToSite}
                className="w-full bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold py-3 sm:py-4 px-4 rounded-xl transition-all duration-200 flex items-center justify-center gap-2 transform hover:scale-[1.02] active:scale-[0.98]"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18"></path>
                </svg>
                Get back to site
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default TemplatesPage;
